import { isStringNullOrWhiteSpace } from 'owa-localize';
import { lazyShowFeedbackDialog } from 'owa-readwrite-recipient-well-internal-feedback';
import updateBindingPromoClicked from '../actions/updateBindingPromoClicked';
import SuggestionsHeaderFooterInfo from '../components/SuggestionsHeaderFooterInfo';
import onLinkedInBindWindowClosed from '../utils/onLinkedInBindWindowClosed';
import shouldShowHeaderFooterItem from '../utils/shouldShowHeaderFooterItem';
import { ITheme } from '@uifabric/styling';
import { logUsage } from 'owa-analytics';
import { getSessionId } from 'owa-config';
import { getGuid } from 'owa-guid';
import { connectToLinkedInLazy } from 'owa-linkedin';
import findReadWriteRecipient from 'owa-readwrite-recipient-well/lib/actions/findReadWriteRecipient';
import { FindRecipientInfoType } from 'owa-recipient-common/lib/components/FindRecipientInfo';
import FindControlViewState from 'owa-recipient-types/lib/types/FindControlViewState';

import {
    FloatingPeoplePicker,
    IBaseFloatingPickerSuggestionProps,
    ISuggestionsHeaderFooterProps,
} from 'office-ui-fabric-react/lib/FloatingPicker';

const styles = require('../components/MaskRecipientButton.scss');

export default function getSuggestionProps(
    getViewState: () => FindControlViewState,
    getFindControl: () => FloatingPeoplePicker,
    controlledComponent: boolean,
    theme?: ITheme
): IBaseFloatingPickerSuggestionProps {
    // className isn't explicitly exposed on the whitelisted props passed to the
    // Suggestion by the ExtendedPicker.
    //
    // Sneaking this property on won't be necessary once Suggestions is moved to a
    // renderprops + inversion of control design
    const extraProps = { className: styles.containsCustomMaskRecipientButton } as {};
    return {
        ...extraProps,
        showRemoveButtons: true,
        headerItemsProps: getHeaderItemProps(getViewState, getFindControl, theme),
        footerItemsProps: getFooterItemProps(
            getViewState,
            getFindControl,
            controlledComponent,
            theme
        ),
        shouldSelectFirstItem: () => {
            return (
                !isStringNullOrWhiteSpace(getViewState().queryString) &&
                getViewState().findResultSet.length > 0
            );
        },
    };
}

function getHeaderItemProps(
    getViewState: () => FindControlViewState,
    getFindControl: () => FloatingPeoplePicker,
    theme?: ITheme
): ISuggestionsHeaderFooterProps[] {
    return [
        {
            renderItem: renderInfo(getViewState(), FindRecipientInfoType.TypeToSearch, theme),
            shouldShow: shouldShowHeaderFooterItem(
                FindRecipientInfoType.TypeToSearch,
                getViewState()
            ),
        },
        {
            renderItem: renderInfo(getViewState(), FindRecipientInfoType.SuggestedContacts, theme),
            shouldShow: shouldShowHeaderFooterItem(
                FindRecipientInfoType.SuggestedContacts,
                getViewState()
            ),
        },
        {
            renderItem: renderInfo(getViewState(), FindRecipientInfoType.UseAddress, theme),
            shouldShow: shouldShowHeaderFooterItem(
                FindRecipientInfoType.UseAddress,
                getViewState()
            ),
            onExecute: () => {
                if (getFindControl()) {
                    getFindControl().forceResolveSuggestion();
                }
            },
        },
    ];
}

function getFooterItemProps(
    getViewState: () => FindControlViewState,
    getFindControl: () => FloatingPeoplePicker,
    controlledComponent: boolean,
    theme?: ITheme
): ISuggestionsHeaderFooterProps[] {
    return [
        {
            renderItem: renderInfo(getViewState(), FindRecipientInfoType.Searching, theme),
            shouldShow: shouldShowHeaderFooterItem(FindRecipientInfoType.Searching, getViewState()),
        },
        {
            renderItem: renderInfo(getViewState(), FindRecipientInfoType.SearchDirectory, theme),
            shouldShow: shouldShowHeaderFooterItem(
                FindRecipientInfoType.SearchDirectory,
                getViewState()
            ),
            onExecute: () => {
                findReadWriteRecipient(
                    getViewState(),
                    getViewState().queryString,
                    true /*searchDirectory*/
                ).then(() => {
                    !controlledComponent &&
                        getFindControl().updateSuggestions(
                            getViewState().findResultSet.slice(),
                            true /*forceUpdate*/
                        );
                });
            },
        },
        {
            renderItem: renderInfo(getViewState(), FindRecipientInfoType.TopNResults, theme),
            shouldShow: shouldShowHeaderFooterItem(
                FindRecipientInfoType.TopNResults,
                getViewState()
            ),
        },
        {
            renderItem: renderInfo(
                getViewState(),
                FindRecipientInfoType.LinkedInAccountBind,
                theme,
                getFindControl()
            ),
            shouldShow: shouldShowHeaderFooterItem(
                FindRecipientInfoType.LinkedInAccountBind,
                getViewState()
            ),
            onExecute: () => {
                getFindControl().hidePicker();
                const correlationId = getGuid();
                connectToLinkedInLazy.importAndExecute(
                    { correlationId: correlationId },
                    onLinkedInBindWindowClosed
                );
                updateBindingPromoClicked(true /*bindingPromClicked*/);
                logUsage('RWLinkedInBindingClicked', {
                    scenario: 'UniversalDirectory',
                    app: 'Outlook',
                    page: 'Compose',
                    control: 'PeoplePicker',
                    environment: 'Web',
                    app_instance: getSessionId(),
                    page_instance: getGuid(),
                    correlation_id: correlationId,
                });
            },
        },
        {
            renderItem: renderInfo(getViewState(), FindRecipientInfoType.FindPeopleFeedback, theme),
            shouldShow: () => {
                return (
                    controlledComponent &&
                    shouldShowHeaderFooterItem(
                        FindRecipientInfoType.FindPeopleFeedback,
                        getViewState()
                    )()
                );
            },
            onExecute: () => {
                lazyShowFeedbackDialog.import().then(showFeedbackDialog => {
                    showFeedbackDialog(() => {
                        return getViewState().peopleFeedbackState;
                    });
                });
            },
        },
    ];
}

function renderInfo(
    viewState: FindControlViewState,
    infoType: FindRecipientInfoType,
    theme?: ITheme,
    picker?: FloatingPeoplePicker
): () => JSX.Element {
    return function(): JSX.Element {
        return SuggestionsHeaderFooterInfo({
            queryString: viewState.queryString,
            numberOfResults: viewState.findResultSet.length,
            infoType: infoType,
            theme: theme,
            picker: picker,
        });
    };
}
